import React, { Suspense } from 'react';

import { QueryClient, QueryClientConfig, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/* -------------------------------------------------------------------------- */
/*                        Global Query Client Defaults                        */
/* -------------------------------------------------------------------------- */
/**
 * These are the global defaults for all query clients.
 * If you need to override a specific param for a particular query,
 * do so in your query usage (e.g. useQuery options).
 * If a setting should apply to all requests, update it here.
 */
const defaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Data is considered fresh for 1 minute by default.
      staleTime: 60 * 1000,
      // Disable automatic refetch on window focus.
      refetchOnWindowFocus: false,
      // Retry failed requests twice before surfacing an error.
      retry: 2,
    },
  },
};

/**
 * Shared singleton instance so that all components using this wrapper
 * interact with the same QueryClient.
 */
export const queryClient = new QueryClient(defaultQueryClientConfig);

/**
 * Provider component that supplies the shared `queryClient` instance to React.
 */
export function QueryProvider({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <Suspense>
        <ReactQueryDevtools initialIsOpen={false} />
      </Suspense>
    </QueryClientProvider>
  );
}
